import endpoints from '@app/src/api/endpoints'
import { FetchKey } from '@app/src/api/fetchHooks'
import { useCreateResource } from '@app/src/api/updateHooks'
import DrawerView from '@app/src/components/Drawer/DrawerView'
import { useSnackbar } from '@app/src/context/SnackbarContext'
import useErrorNotification from '@app/src/hooks/errorNotification'
import usePreserveScrollPosition from '@app/src/hooks/usePreserveScrollPosition'
import { NotificationSeverity } from '@app/src/wf-constants'
import { FormControl, FormControlLabel, Radio, RadioGroup, Stack, TextField, Typography } from '@mui/material'
import { useQueryClient } from '@tanstack/react-query'
import React from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useIntl } from 'react-intl'
import { useDrawer } from '../DrawerContext'

enum ReportReason {
  COMPANY_REFERENCED_INCORRECT = 'CompanyReferencedIncorrect',
  SOURCE_INCORRECT = 'SourceIncorrect',
  CITATION_INCORRECT = 'CitationIncorrect',
  OTHER = 'Other',
}

interface DiscardAnswerFormData {
  reason: ReportReason | ''
  otherReasonText: string
}

export type DrawerViewDiscardAnswerProps = {
  responseItemId: number
  responseId: number
}

const DrawerViewDiscardAnswer: React.FC<DrawerViewDiscardAnswerProps> = ({ responseItemId, responseId }) => {
  const { formatMessage } = useIntl()
  const queryClient = useQueryClient()
  const { showSnackbar } = useSnackbar()
  const { showErrorNotification } = useErrorNotification()
  const { closeDrawer } = useDrawer()
  const preserveScrollPosition = usePreserveScrollPosition()

  const { control, handleSubmit, watch, register } = useForm<DiscardAnswerFormData>({
    defaultValues: {
      reason: '',
      otherReasonText: '',
    },
  })

  const selectedReason = watch('reason')
  const otherReasonText = watch('otherReasonText')

  const { mutateAsync: discardAiAnswer, isPending: isLoading } = useCreateResource()

  const submit = async (data: DiscardAnswerFormData) => {
    if (!data.reason) return
    if (data.reason === ReportReason.OTHER && !data.otherReasonText.trim()) return

    await discardAiAnswer(
      {
        url: endpoints.discardAiAnswer(responseItemId),
        body: {
          reason: data.reason,
          ...(data.reason === ReportReason.OTHER && { otherReasonText: data.otherReasonText.trim() }),
        },
      },
      {
        onSuccess: () => {
          preserveScrollPosition(async () => {
            await Promise.all([
              queryClient.invalidateQueries({ queryKey: [FetchKey.Response, responseId] }),
              queryClient.invalidateQueries({ queryKey: [FetchKey.ResponseItemsCollection] }),
              queryClient.invalidateQueries({ queryKey: [FetchKey.Answer] }),
              queryClient.invalidateQueries({ queryKey: [FetchKey.ScrapedAnswer] }),
              queryClient.invalidateQueries({ queryKey: [FetchKey.FindingsProviders] }),
            ])
          })
          showSnackbar({
            message: formatMessage({ id: 'reporting.discardAiAnswer.success' }),
            severity: NotificationSeverity.success,
          })
          closeDrawer()
        },
        onError: error => {
          showErrorNotification({ requestError: error })
        },
      },
    )
  }

  return (
    <DrawerView
      title={formatMessage({ id: 'reporting.discardAiAnswer.title' })}
      stackButtons
      buttons={[
        {
          label: formatMessage({ id: 'general.cancel' }),
          variant: 'text',
          onClick: closeDrawer,
        },
        {
          label: formatMessage({ id: 'reporting.discardAiAnswer.confirm' }),
          variant: 'contained',
          onClick: handleSubmit(submit),
          loading: isLoading,
          disabled: !selectedReason || (selectedReason === ReportReason.OTHER && !otherReasonText.trim()),
        },
      ]}
    >
      <Stack spacing={3} px={3}>
        <Typography variant="subtitle1">{formatMessage({ id: 'reporting.discardAiAnswer.description' })}</Typography>

        <FormControl component="fieldset">
          <Controller
            name="reason"
            control={control}
            render={({ value, onChange }) => (
              <RadioGroup value={value} onChange={onChange} sx={{ gap: 2 }}>
                <FormControlLabel
                  value={ReportReason.COMPANY_REFERENCED_INCORRECT}
                  control={<Radio />}
                  label={formatMessage({ id: 'reporting.discardAiAnswer.reasons.companyIncorrect' })}
                />
                <FormControlLabel
                  value={ReportReason.SOURCE_INCORRECT}
                  control={<Radio />}
                  label={formatMessage({ id: 'reporting.discardAiAnswer.reasons.sourceIncorrect' })}
                />
                <FormControlLabel
                  value={ReportReason.CITATION_INCORRECT}
                  control={<Radio />}
                  label={formatMessage({ id: 'reporting.discardAiAnswer.reasons.citationIncorrect' })}
                />
                <FormControlLabel
                  value={ReportReason.OTHER}
                  control={<Radio />}
                  label={formatMessage({ id: 'reporting.discardAiAnswer.reasons.other' })}
                />
              </RadioGroup>
            )}
          />
        </FormControl>

        {selectedReason === ReportReason.OTHER && (
          <TextField
            name="otherReasonText"
            fullWidth
            multiline
            rows={3}
            inputProps={{ maxLength: 500 }}
            placeholder={formatMessage({ id: 'reporting.discardAiAnswer.otherPlaceholder' })}
            variant="filled"
            inputRef={register({ required: formatMessage({ id: 'form.validation.required' }) })}
          />
        )}
      </Stack>
    </DrawerView>
  )
}

export default DrawerViewDiscardAnswer
